import Vue from 'vue'
import VueLocalStorage from 'vue-local-storage'
import VueMeta from 'vue-meta'
import nprogress from 'nprogress'
import anime from 'animejs'
import isMobile from 'ismobilejs'

import MapWithPopup from './map-with-popup.vue'
import Card from './card.vue'
import ShowPhone from './show-phone.vue'
import NumberInputWithSuggestions from './number-input-with-suggestions.vue'
import AsyncForm from './async-form.vue'
import DetailCarousel from './detail-carousel.vue'

import cardCarouselMixin from './mixins/card-carousel-mixin'
import showMapMixin from './mixins/show-map-mixin'
import displayPricesMixin from './mixins/display-prices-mixin'
import popupsMixin from './mixins/popups-mixin'
import filtersMixin from './mixins/filters-mixin'
import toggleCursorMixin from './mixins/toggle-cursor-mixin'
import publicPathMixin from './mixins/public-path-mixin'
import windowWidthMixin from './mixins/window-width-mixin'

import toggleScroll from '../toggle-scroll'

Vue.use(VueLocalStorage)
Vue.use(VueMeta)

export default (root, mounted) => {
  new Vue({
    components: {
      MapWithPopup,
      Card,
      ShowPhone,
      NumberInputWithSuggestions,
      AsyncForm,
      DetailCarousel,
    },
    el: root,
    mixins: [
      cardCarouselMixin,
      showMapMixin,
      displayPricesMixin,
      popupsMixin,
      filtersMixin,
      toggleCursorMixin,
      publicPathMixin,
      windowWidthMixin,
    ],
    data() {
      return {
        detail: null,
        showDetail: false,
        showWeSearchPopupByDefault:
          this.$localStorage.get('weSearchPopupHasBeenDismissed') === null,
        hasMounted: true,
        currentAmenityType: null,
      }
    },
    mounted() {
      mounted()

      const detail =
        'detail' in this.$root.$el.dataset
          ? JSON.parse(this.$root.$el.dataset.detail)
          : null
      this.detail = detail
      this.showDetail = detail !== null

      const popupElement = document.getElementById('we-search-for-you-popup')
      if (
        popupElement &&
        this.showWeSearchPopupByDefault &&
        !detail &&
        !isMobile().any
      ) {
        this.pickPopup('we-search-for-you', false)
      }

      this.hasMounted = true

      window.addEventListener('popstate', this.syncDetailStateToUrl)
    },
    beforeDestroy() {
      window.removeEventListener('popstate', this.syncDetailStateToUrl)
    },
    methods: {
      disableShowingWeSearchPopup() {
        this.$localStorage.set('weSearchPopupHasBeenDismissed', true)
      },
      dismissWeSearchPopup() {
        this.disableShowingWeSearchPopup()
        this.closePopup()
      },
      getDetailUrl(slug) {
        return `${this.currentRoute}/${slug}${window.location.search}`
      },
      openDetail(slug, updateUrl = true) {
        if (updateUrl) {
          window.history.pushState(null, '', this.getDetailUrl(slug))
        }
        this.showDetail = true
        this.detail = null
        this.currentAmenityType = null
        nprogress.start()
        fetch(`${this.currentRoute}/${slug}?json=1`)
          .then(res => res.json())
          .then(json => {
            this.detail = json.detail
            this.seo = json.seo
            nprogress.done()
          })
      },
      onMapPopupClick(card) {
        this.openDetail(card.url)
      },
      closeDetail(updateUrl = true) {
        this.showDetail = false
        if (updateUrl) {
          window.history.pushState(
            '',
            '',
            `${this.currentRoute}${window.location.search}`
          )
        }

        this.updateMarkersOnMap({ updateSeo: true, updateResults: false })

        setTimeout(() => {
          this.detail = null
        }, 800)
      },
      syncDetailStateToUrl() {
        const parts = window.location.pathname.split(this.currentRoute)
        const slug = parts[1] && parts[1].substr(1)
        if (slug) {
          this.openDetail(slug, false)
        } else {
          this.closeDetail(false)
        }
      },
      makeSpaceForDropdown() {
        const targets = [document.documentElement, document.body]
        const scrollY = window.scrollY || document.documentElement.scrollTop
        const minScrollTop = 200
        if (scrollY < minScrollTop) {
          anime.remove(targets)
          anime({
            targets,
            duration: 400,
            scrollTop: minScrollTop,
            easing: 'easeOutQuad',
          })
        }
      },
    },
    localStorage: {
      weSearchPopupHasBeenDismissed: {
        type: Boolean,
        default: true,
      },
    },
    metaInfo() {
      return {
        title: this.seo.title,
        meta: [
          {
            vmid: 'description',
            name: 'description',
            content: this.seo.metaDescription,
          },
        ],
      }
    },
    watch: {
      showDetail() {
        toggleScroll(this.showDetail === false, this.$refs.detailScroller)
      },
    },
    computed: {
      availableAmenityTypes() {
        if (!this.detail) {
          return []
        }
        return this.detail.amenities
          .reduce((acc, curr) => {
            if (!acc.includes(curr.type)) {
              acc.push(curr.type)
            }
            return acc
          }, [])
          .sort()
      },
      filteredAmenities() {
        if (!this.detail) {
          return []
        }
        const result = this.detail.amenities.filter(
          el =>
            this.currentAmenityType == null ||
            el.type === this.currentAmenityType
        )
        result.push({ ...this.detail, zIndex: 10000 })
        return result
      },
      coverFormSelectionLimit() {
        if (this.windowHeight < 660) {
          return 1
        }
        if (this.windowWidth < 640) {
          return 1
        }
        if (this.windowWidth < 1024) {
          return 4
        }
        if (this.windowWidth < 1200) {
          return 1
        }
        return 4
      },
      youtubeIframe() {
        if (!this.detail || !this.detail.videoUrl) {
          return null
        }

        const match = this.detail.videoUrl.match(
          /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/ // eslint-disable-line
        )
        if (match && match[2].length === 11) {
          return `//www.youtube.com/embed/${match[2]}?autoplay=1&mute=1`
        }
        return null
      },
    },
  })
}
